/**
 * TravelingDateTime — date & time picker with the Recommended-Pickup entry point.
 *
 * Differences from production:
 *   • Layout runs selected-value pill → wheel card → ride meta → "OR" divider →
 *     recommendation card. The production "little-title" heading is dropped (the
 *     modal header already names the step).
 *   • The old black "Smart Pickup Time" card above the wheel is replaced by a
 *     light "Get a Recommended Pickup Time" card BELOW it, and the drive/drop-off
 *     meta row moved out of that card to sit directly under the wheel, since it
 *     describes the picked time rather than the recommendation.
 *   • The card routes to the new `smartPickupRecommendation` step (the 3-way
 *     "how would you like to get your pickup recommendation?" chooser) instead
 *     of the production `smartTimeSelect`.
 *   • On mount, if STORAGE_KEYS.SMART_PICKUP_SCROLL_PICKER is set (returning from a "Set
 *     Pickup Time Manually" action), the view scrolls down to the wheel.
 *
 * The date/time wheel logic is copied verbatim from production.
 */
import {serverDirectionFor} from '@/utils/serverDirection';
import React, {useState, useEffect, useMemo, useRef} from "react";
import {WheelPicker, WheelPickerWrapper} from "@ncdai/react-wheel-picker";
import ModalHeader from "@/customComponents/uiComponents/modal/ModalHeader.jsx";
import {ModalFooter} from "../Components/BookingModalShell.jsx";
import {getHopnMinDate, getHopnMaxDate} from "@/utils/filesConstants.js";
import {isNearAirport} from "@/utils/airports";
import {tripService, routeScheduledAt, toScheduledString} from "@/services/tripService";
import {Loader} from "@/customComponents/uiComponents/Loader.jsx";
import {BOOKING_CONSTRAINTS} from "@/constants/booking";
import {tripNow, formatTripLocalTime, parseRideDate} from "@/utils/dateUtils.js";
import {storage, STORAGE_KEYS} from "@/utils/storage";
import {img} from "@/utils/pluginImg.js";

const formatDateLabel = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d.toLocaleDateString("en-US", {weekday: "short", month: "short", day: "numeric"});
};

const buildDateOptions = () => {
    const min = getHopnMinDate();
    const max = getHopnMaxDate();
    min.setHours(0, 0, 0, 0);
    max.setHours(0, 0, 0, 0);
    const dates = [];
    const current = new Date(min);
    while (current <= max) {
        dates.push({iso: current.toISOString(), label: formatDateLabel(current)});
        current.setDate(current.getDate() + 1);
    }
    return dates;
};

const HOUR_OPTIONS = Array.from({length: 12}, (_, i) => {
    const v = String(i + 1);
    return {value: v, label: v};
});
const MINUTE_OPTIONS = Array.from({length: 12}, (_, i) => {
    const v = String(i * 5).padStart(2, "0");
    return {value: v, label: v};
});
const PERIOD_OPTIONS = [
    {value: "AM", label: "AM"},
    {value: "PM", label: "PM"},
];

const WHEEL_CLASS_NAMES = {
    optionItem: "hopn-wheel-option",
    highlightWrapper: "hopn-wheel-highlight",
    highlightItem: "hopn-wheel-highlight-item",
};
const FIRST_WHEEL_CLASS_NAMES = {
    optionItem: "hopn-wheel-option first",
    highlightWrapper: "hopn-wheel-highlight",
    highlightItem: "hopn-wheel-highlight-item first",
};

/**
 * How far ahead the wheel opens when nothing has been picked yet, in days from today:
 * 2 = the day AFTER tomorrow, i.e. the third date in the list. Was 1 (tomorrow) until
 * 2026-08-27.
 */
const DEFAULT_DATE_OFFSET_DAYS = 2;

/**
 * Which date the wheel opens on when nothing has been picked yet — see
 * DEFAULT_DATE_OFFSET_DAYS.
 *
 * `dateOptions[0]` is today (or already tomorrow, when today's last bookable slot has
 * passed — see getHopnMinDate), so this takes the first option that isn't before the
 * target rather than blindly indexing [n]. Falls back to the first option if the range
 * somehow doesn't reach that far.
 */
const defaultDateLabel = (dateOptions) => {
    const target = tripNow();
    target.setDate(target.getDate() + DEFAULT_DATE_OFFSET_DAYS);
    target.setHours(0, 0, 0, 0);
    const match = dateOptions.find((opt) => {
        const d = new Date(opt.iso);
        d.setHours(0, 0, 0, 0);
        return d.getTime() >= target.getTime();
    });
    return (match || dateOptions[0])?.label || "";
};

const parseStoredDateTime = (dateOptions) => {
    const storedDate = storage.get(STORAGE_KEYS.RIDE_DATE);
    const storedTime = storage.get(STORAGE_KEYS.RIDE_TIME);

    let dateLabel = defaultDateLabel(dateOptions);

    const now = tripNow();
    let mins = now.getMinutes();
    const remainder = mins % 5;
    if (remainder !== 0) mins = mins + (5 - remainder);
    if (mins >= 60) {
        mins = 0;
        now.setHours(now.getHours() + 1);
    }
    let h = now.getHours();
    let period = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    let hour = String(h);
    let minute = String(mins).padStart(2, "0");

    if (storedDate) {
        // parseRideDate, not new Date(): the stored value is a bare "YYYY-MM-DD", which
        // new Date() reads as UTC midnight. West of UTC that lands on the PREVIOUS local
        // day, no option matched, and coming back to this step silently reset the wheel
        // to defaultDateLabel instead of the date the passenger had picked.
        const d = parseRideDate(storedDate);
        if (!d) return {date: dateLabel, hour, minute, period};
        d.setHours(0, 0, 0, 0);
        const match = dateOptions.find((opt) => {
            const optD = new Date(opt.iso);
            optD.setHours(0, 0, 0, 0);
            return optD.getTime() === d.getTime();
        });
        if (match) dateLabel = match.label;
    }

    if (storedTime) {
        const t = new Date(storedTime);
        let hh = t.getHours();
        const m = t.getMinutes();
        period = hh >= 12 ? "PM" : "AM";
        hh = hh % 12 || 12;
        hour = String(hh);
        const rounded = Math.round(m / 5) * 5;
        minute = String(rounded >= 60 ? 55 : rounded).padStart(2, "0");
    }

    return {date: dateLabel, hour, minute, period};
};

/** Convert 12h hour + period to 24h */
const to24h = (hour, period) => {
    const h = parseInt(hour, 10);
    if (period === "AM") return h === 12 ? 0 : h;
    return h === 12 ? 12 : h + 12;
};

/** Local YYYY-MM-DD from an ISO date-option value (avoids UTC day roll-back). */
const localYMD = (iso) => {
    const d = new Date(iso);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/** "40 min" / "1 hr 5 min" / "2 hr" */
const formatDurationHM = (minutes) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h === 0) return `${m} min`;
    if (m === 0) return `${h} hr`;
    return `${h} hr ${m} min`;
};

export function TravelingDateTime(props) {
    const dateOptions = useMemo(() => buildDateOptions(), []);
    const datePickerOptions = useMemo(() => dateOptions.map((d) => ({value: d.label, label: d.label})), [dateOptions]);

    const [pickerValue, setPickerValue] = useState(() => parseStoredDateTime(dateOptions));
    const [recalculate] = useState(false);
    const [quickBookingError, setQuickBookingError] = useState("");
    // fromAirport requires a flight number before the fare can be checked — the
    // pickup depends on when the plane actually lands. Shown over the submit button.
    const [showFlightTooltip, setShowFlightTooltip] = useState(false);
    const pickerRef = useRef(null);
    const errorRef = useRef(null);

    // Detect if pickup or drop-off is near an airport.
    // Destinations layout: [pickup, dropoff, ...stopovers] — dropoff is index 1.
    const destinations = storage.get(STORAGE_KEYS.DESTINATIONS, []);
    const firstDest = destinations[0];
    const lastDest = destinations[1];
    // The server's direction (switch on) wins; otherwise our own coordinate match.
    const serverDir = serverDirectionFor(destinations[0], destinations[1])?.direction;
    const isFromAirport = serverDir ? serverDir === 'from_airport' : (firstDest?.lat && firstDest?.lng ? !!isNearAirport(firstDest.lat, firstDest.lng) : false);
    const isToAirport = serverDir ? serverDir === 'to_airport' : (lastDest?.lat && lastDest?.lng ? !!isNearAirport(lastDest.lat, lastDest.lng) : false);
    const isAirportTrip = isFromAirport || isToAirport;

    // Drive time for the meta row. Seeded from the stored route so there is something to
    // show on the first paint, then replaced by the traffic-aware duration for whatever
    // pickup time is on the wheel — see the re-quote effect below.
    const [driveMinutes, setDriveMinutes] = useState(
        () => storage.get(STORAGE_KEYS.ROUTE_BREAKDOWN, null)?.duration_minutes || null
    );
    const [routeQuoting, setRouteQuoting] = useState(false);

    const isToday = useMemo(() => {
        const dateOpt = dateOptions.find((d) => d.label === pickerValue.date);
        if (!dateOpt) return false;
        const sel = new Date(dateOpt.iso);
        sel.setHours(0, 0, 0, 0);
        const today = tripNow();
        today.setHours(0, 0, 0, 0);
        return sel.getTime() === today.getTime();
    }, [pickerValue.date, dateOptions]);

    const nowRef = useMemo(() => {
        const now = tripNow();
        let m = now.getMinutes();
        const rem = m % 5;
        if (rem !== 0) m = m + (5 - rem);
        if (m >= 60) {
            m = 0;
            now.setHours(now.getHours() + 1);
        }
        now.setMinutes(m, 0, 0);
        return {hour24: now.getHours(), minute: now.getMinutes()};
    }, [pickerValue.date]);

    const filteredPeriodOptions = useMemo(() => {
        if (!isToday) return PERIOD_OPTIONS;
        if (nowRef.hour24 >= 12) return [{value: "PM", label: "PM"}];
        return PERIOD_OPTIONS;
    }, [isToday, nowRef]);

    const filteredHourOptions = useMemo(() => {
        if (!isToday) return HOUR_OPTIONS;
        return HOUR_OPTIONS.filter((opt) => {
            const h24 = to24h(opt.value, pickerValue.period);
            if (h24 > nowRef.hour24) return true;
            if (h24 < nowRef.hour24) return false;
            return nowRef.minute < 60;
        });
    }, [isToday, nowRef, pickerValue.period]);

    const filteredMinuteOptions = useMemo(() => {
        if (!isToday) return MINUTE_OPTIONS;
        const selected24 = to24h(pickerValue.hour, pickerValue.period);
        if (selected24 > nowRef.hour24) return MINUTE_OPTIONS;
        if (selected24 < nowRef.hour24) return MINUTE_OPTIONS;
        return MINUTE_OPTIONS.filter((opt) => parseInt(opt.value, 10) >= nowRef.minute);
    }, [isToday, nowRef, pickerValue.hour, pickerValue.period]);

    useEffect(() => {
        const synced = parseStoredDateTime(dateOptions);
        setPickerValue(synced);
    }, []);

    // Returning from a "Set Pickup Time Manually" action → scroll to the wheel.
    useEffect(() => {
        if (storage.get(STORAGE_KEYS.SMART_PICKUP_SCROLL_PICKER)) {
            storage.remove(STORAGE_KEYS.SMART_PICKUP_SCROLL_PICKER);
            setTimeout(() => {
                pickerRef.current?.scrollIntoView({behavior: "smooth", block: "center"});
            }, 250);
        }
    }, []);

    // When the lead-time warning appears, scroll it into view so the user sees it.
    useEffect(() => {
        if (quickBookingError) {
            setTimeout(() => {
                errorRef.current?.scrollIntoView({behavior: "smooth", block: "center"});
            }, 60);
        }
    }, [quickBookingError]);

    useEffect(() => {
        if (filteredPeriodOptions.length && !filteredPeriodOptions.find((o) => o.value === pickerValue.period)) {
            setPickerValue((prev) => ({...prev, period: filteredPeriodOptions[0].value}));
        }
    }, [filteredPeriodOptions]);

    useEffect(() => {
        if (filteredHourOptions.length && !filteredHourOptions.find((o) => o.value === pickerValue.hour)) {
            setPickerValue((prev) => ({...prev, hour: filteredHourOptions[0].value}));
        }
    }, [filteredHourOptions]);

    useEffect(() => {
        if (filteredMinuteOptions.length && !filteredMinuteOptions.find((o) => o.value === pickerValue.minute)) {
            setPickerValue((prev) => ({...prev, minute: filteredMinuteOptions[0].value}));
        }
    }, [filteredMinuteOptions]);

    const pickerReady =
        filteredPeriodOptions.length > 0 &&
        filteredHourOptions.length > 0 &&
        filteredMinuteOptions.length > 0 &&
        filteredPeriodOptions.some((o) => o.value === pickerValue.period) &&
        filteredHourOptions.some((o) => o.value === pickerValue.hour) &&
        filteredMinuteOptions.some((o) => o.value === pickerValue.minute);

    // Clear error / flight prompt when the user changes picker values
    useEffect(() => {
        if (quickBookingError) setQuickBookingError("");
        if (showFlightTooltip) setShowFlightTooltip(false);
    }, [pickerValue.date, pickerValue.hour, pickerValue.minute, pickerValue.period]);

    const buildDateTimeFromPicker = () => {
        const dateOpt = dateOptions.find((d) => d.label === pickerValue.date);
        if (!dateOpt) return null;
        const base = new Date(dateOpt.iso);
        let h = parseInt(pickerValue.hour, 10);
        const m = parseInt(pickerValue.minute, 10);
        if (pickerValue.period === "AM" && h === 12) h = 0;
        if (pickerValue.period === "PM" && h !== 12) h += 12;
        base.setHours(h, m, 0, 0);
        return base;
    };

    // ── Traffic-aware re-quote ────────────────────────────────────────────────
    // The drive time depends on WHEN the trip runs: the same route at 6pm can take half
    // again as long as at 2am. /calculate-route returns the duration predicted for the
    // pickup window it is given, so the route is re-quoted on arrival and again on every
    // change to the wheel — date, hour, minute or AM/PM — and the meta row shows a
    // skeleton until the answer lands.
    //
    // The fresh hash is stored too, not just the duration: the backend buckets route_hash
    // by pickup window, so the hash that matches the chosen time is the one Check Fare
    // and the booking must carry.
    const routeReqRef = useRef(0);
    useEffect(() => {
        const origin = destinations[0];
        const destination = destinations[1];
        if (!origin?.lat || !destination?.lat) return;

        const dt = buildDateTimeFromPicker();
        if (!dt) return;
        const pickupAt = toScheduledString(dt);

        // Already quoted for exactly this window — no request, no skeleton. This is what
        // stops a plain visit to this step (arriving, or coming back from the pickup
        // recommendation, which quotes the route itself when it commits a time) from
        // re-measuring a route nothing has changed about.
        if (pickupAt === routeScheduledAt()) {
            setRouteQuoting(false);
            return;
        }

        const reqId = ++routeReqRef.current;
        setRouteQuoting(true);
        // Debounced: spinning the wheel fires a change per notch, and only the value the
        // passenger lands on is worth quoting.
        const timer = setTimeout(async () => {
            const result = await tripService.calculateRoute(
                origin, destination, destinations.slice(2), {scheduledPickupAt: pickupAt}
            );
            // A slower earlier quote must not overwrite a newer one.
            if (reqId !== routeReqRef.current) return;
            setRouteQuoting(false);
            if (!result.success) return;   // keep the previous duration rather than blanking it
            const hash = result.data?.route_hash ?? result.data?.routeHash;
            const routeData = result.data?.route_data ?? result.data?.routeData;
            if (hash) storage.set(STORAGE_KEYS.ROUTE_HASH, hash);
            if (routeData) {
                storage.set(STORAGE_KEYS.ROUTE_BREAKDOWN, routeData);
                setDriveMinutes(routeData.duration_minutes || null);
            }
        }, 400);

        return () => clearTimeout(timer);
    }, [pickerValue.date, pickerValue.hour, pickerValue.minute, pickerValue.period]);

    // "Drop-off time" for the smart card meta = pickup + drive time.
    const dropOffLabel = useMemo(() => {
        if (!driveMinutes) return null;
        const dt = buildDateTimeFromPicker();
        if (!dt) return null;
        const dropoff = new Date(dt.getTime() + driveMinutes * 60000);
        return dropoff.toLocaleTimeString("en-US", {hour: "numeric", minute: "2-digit"});
    }, [pickerValue, driveMinutes]);

    const validateLeadTime = (dateTime) => {
        const now = tripNow();
        const minLeadMs = BOOKING_CONSTRAINTS.MINIMUM_BOOKING_LEAD_TIME_MINUTES * 60 * 1000;
        const minAllowed = new Date(now.getTime() + minLeadMs + 10 * 60 * 1000);
        const contactNumber = BOOKING_CONSTRAINTS.CONTACT_NUMBER;
        const mins = BOOKING_CONSTRAINTS.MINIMUM_BOOKING_LEAD_TIME_MINUTES;

        if (dateTime < minAllowed) {
            const hours = Math.floor(mins / 60);
            const remMins = mins % 60;
            const leadTimeStr = remMins === 0
                ? `${hours} Hour${hours !== 1 ? "s" : ""}`
                : `${hours} Hour${hours !== 1 ? "s" : ""} ${remMins} Minute${remMins !== 1 ? "s" : ""}`;
            const earliestMins = minAllowed.getMinutes();
            const remainder = earliestMins % 5;
            if (remainder !== 0) minAllowed.setMinutes(earliestMins + (5 - remainder), 0, 0);
            const earliestTime = formatTripLocalTime(minAllowed);
            setQuickBookingError(
                `For quick bookings, please contact ${contactNumber}. Online booking is only available ${leadTimeStr} in advance (earliest available: ${earliestTime}).`
            );
            return false;
        }
        return true;
    };

    // Smart Pickup card → the recommendation-method chooser. Same lead-time gate
    // as Check Fare, and commit the picked date so the chooser/flight lookup has it.
    /**
     * Persist what the wheel is showing as the flow's date/time.
     *
     * The wheel's value lives in component state until submit, so any screen opened from
     * here used to read whatever was in storage from BEFORE — which on a first visit is
     * nothing, and the recommendation chooser fell back to a hardcoded 12:00 PM while the
     * wheel plainly showed 8:05 AM. Committing both parts on the way out makes the value
     * on screen the value everything else prefills from.
     */
    const commitPickerToStorage = () => {
        const dateOpt = dateOptions.find((d) => d.label === pickerValue.date);
        if (dateOpt) storage.set(STORAGE_KEYS.RIDE_DATE, localYMD(dateOpt.iso));
        const dateTime = buildDateTimeFromPicker();
        if (dateTime) storage.set(STORAGE_KEYS.RIDE_TIME, dateTime.toString());
    };

    const openRecommendation = () => {
        setQuickBookingError("");
        const dateTime = buildDateTimeFromPicker();
        if (dateTime && !validateLeadTime(dateTime)) return;
        commitPickerToStorage();
        props.setStep("smartPickupRecommendation");
    };

    const handleSubmit = async () => {
        setQuickBookingError("");
        const dateTime = buildDateTimeFromPicker();
        if (!dateTime) return;
        if (!validateLeadTime(dateTime)) return;

        const dateOpt = dateOptions.find((d) => d.label === pickerValue.date);
        storage.set(STORAGE_KEYS.RIDE_DATE, localYMD(dateOpt.iso));
        storage.set(STORAGE_KEYS.RIDE_TIME, dateTime.toString());
        storage.remove(STORAGE_KEYS.TDT_SHOW_WAITING_INFO);

        // From-airport trips REQUIRE a flight number — the pickup has to follow the
        // actual landing, so a hand-picked time alone isn't bookable. To-airport and
        // city trips proceed without one. Checked after the date/time is committed so
        // the value survives the trip through the recommendation chooser.
        if (isFromAirport && !storage.get(STORAGE_KEYS.FLIGHT_NUMBER)) {
            setShowFlightTooltip(true);
            return;
        }

        if (storage.get(STORAGE_KEYS.EDIT_STEP) === true) {
            props.setStep("orderDetails");
        } else {
            if (tripService.needsRecalculation()) {
                storage.remove(STORAGE_KEYS.ALL_CARS_API);
            }
            props.setStep("selectedCarDetails");
        }
    };

    const backStep = storage.get(STORAGE_KEYS.SKIP_LUGGAGE_STEP) === true ? "passengerQuantity" : "passengerLuggage";
    const isEditMode = storage.get(STORAGE_KEYS.EDIT_STEP) === true;

    return (
        <>
            <ModalHeader
                title="Date & Time"
                extraClasses=""
                totalSteps={3}
                cancelButton={false}
                cancelButtonType="text"
                backStep={backStep}
                showBackButton={true}
                setStep={props.setStep}
                closeModal={props.closeModal}
            />

            <div className="hopn-traveling-date-time hopn-sd-date-time modalPages-spacing">
                <div className="hopn-wheel-selected-display" ref={pickerRef}>
                    <span className="hopn-wheel-selected-date">{pickerValue.date}</span>
                    <span className="hopn-wheel-selected-sep"> - </span>
                    <span className="hopn-wheel-selected-time">{pickerValue.hour}:{pickerValue.minute} {pickerValue.period}</span>
                </div>

                <div className="hopn-wheel-picker-wrap">
                    {pickerReady && (
                        <WheelPickerWrapper className="hopn-wheel-picker-columns">
                            <WheelPicker
                                options={datePickerOptions}
                                value={pickerValue.date}
                                onValueChange={(v) => {
                                    setPickerValue((prev) => ({...prev, date: v}));
                                    storage.remove(STORAGE_KEYS.FLIGHT_NUMBER);
                                    storage.remove(STORAGE_KEYS.SMART_TIME_FLIGHT_DETAILS);
                                    storage.remove(STORAGE_KEYS.SMART_TIME_SELECTED);
                                }}
                                optionItemHeight={50}
                                visibleCount={16}
                                classNames={FIRST_WHEEL_CLASS_NAMES}
                            />
                            <WheelPicker
                                options={filteredHourOptions}
                                value={pickerValue.hour}
                                onValueChange={(v) => {
                                    setPickerValue((prev) => ({...prev, hour: v}));
                                    storage.remove(STORAGE_KEYS.SMART_TIME_SELECTED);
                                }}
                                optionItemHeight={50}
                                visibleCount={16}
                                classNames={WHEEL_CLASS_NAMES}
                            />
                            <WheelPicker
                                options={filteredMinuteOptions}
                                value={pickerValue.minute}
                                onValueChange={(v) => {
                                    setPickerValue((prev) => ({...prev, minute: v}));
                                    storage.remove(STORAGE_KEYS.SMART_TIME_SELECTED);
                                }}
                                optionItemHeight={50}
                                visibleCount={16}
                                classNames={WHEEL_CLASS_NAMES}
                            />
                            <WheelPicker
                                options={filteredPeriodOptions}
                                value={pickerValue.period}
                                onValueChange={(v) => {
                                    setPickerValue((prev) => ({...prev, period: v}));
                                    storage.remove(STORAGE_KEYS.SMART_TIME_SELECTED);
                                }}
                                optionItemHeight={50}
                                visibleCount={16}
                                classNames={WHEEL_CLASS_NAMES}
                            />
                        </WheelPickerWrapper>
                    )}
                </div>

                {/* Ride meta — reads off the picked time, so it sits with the wheel
                    rather than inside the recommendation card as it used to. */}
                {routeQuoting ? (
                    // Skeleton while the route is re-quoted for the picked time — the old
                    // duration belongs to a different traffic window, so showing it here
                    // would state a drive time we already know is wrong.
                    <div className="hopn-sd-ride-meta is-loading" aria-busy="true">
                        <span className="hopn-sd-ride-meta-skeleton"/>
                        <span className="hopn-sd-ride-meta-sep">|</span>
                        <span className="hopn-sd-ride-meta-skeleton short"/>
                    </div>
                ) : (dropOffLabel || driveMinutes) && (
                    <div className="hopn-sd-ride-meta">
                        {dropOffLabel && (
                            <span>Drop-off time <strong>{dropOffLabel}</strong></span>
                        )}
                        {dropOffLabel && driveMinutes && <span className="hopn-sd-ride-meta-sep">|</span>}
                        {driveMinutes && (
                            <span>About <strong>{formatDurationHM(driveMinutes)}</strong> ride</span>
                        )}
                    </div>
                )}

                {/* The recommendation route still needs a flight, so it stays
                    airport-only — same gate the old Smart Pickup card used.

                    ⚠️ This is now the ONLY thing `isAirportTrip` gates on this step. The
                    other branch — a two-card perks slider ("Pay after drop off" /
                    "Guaranteed Flat Rate") shown to NON-airport trips, where this
                    recommendation card is not offered — was removed on request
                    (2026-08-31), along with the two popups it was the only opener for.
                    Same change as the booking plugin's copy of this step. The perks
                    themselves are unaffected; they still appear on carSelection, and their
                    `payAfterDropoff` / `noSurprises` steps are still reachable from
                    there. */}
                {isAirportTrip && (
                    <>
                        <div className="hopn-sd-or"><span>OR</span></div>

                        <div
                            className="hopn-sd-recommend-card"
                            onClick={openRecommendation}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    openRecommendation();
                                }
                            }}
                            role="button"
                            tabIndex={0}
                        >
                            <div className="hopn-sd-recommend-text">
                                <h4>Get a Recommended Pickup Time</h4>
                                <p>Based on your flight and live traffic.</p>
                            </div>
                            <span className="hopn-sd-recommend-arrow" aria-hidden="true">
                                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M4.5 12h14M12.5 6l6 6-6 6" stroke="#201e20" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </span>
                        </div>
                    </>
                )}

                {quickBookingError && (
                    <div className="hopn-quick-booking-error" ref={errorRef}>{quickBookingError}</div>
                )}

                <ModalFooter>
                    <div className="hopn-submit-btn-wrapper">
                        <button
                            className={`hopn-add-passenger-button ${isEditMode ? "no-icon" : ""}`}
                            disabled={recalculate}
                            onClick={handleSubmit}
                        >
                            {isEditMode
                                ? (recalculate ? <Loader color="#fff"/> : "Done")
                                : "Check Fare"}
                        </button>
                        {showFlightTooltip && (
                            <div className="hopn-submit-tooltip visible">
                                <div className="tooltip-close-row">
                                    <button type="button" className="tooltip-close-btn" onClick={() => setShowFlightTooltip(false)} aria-label="Close">
                                        <img src={img("bigClose.svg")} alt="Close"/>
                                    </button>
                                </div>
                                {/* Routes to the recommendation chooser, which is where a flight
                                    number is entered now (it replaced smartTimeSelect as this
                                    step's entry point). Commit the picked date first so the
                                    flight lookup there is scoped to the right day. */}
                                <p>Add flight number from <u style={{cursor: 'pointer'}} onClick={() => {
                                    commitPickerToStorage();
                                    props.setStep("smartPickupRecommendation");
                                }}>Smart suggestion</u></p>
                            </div>
                        )}
                    </div>
                </ModalFooter>
            </div>
        </>
    );
}

export default TravelingDateTime;
